import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ReviewForm, OwnerReply } from '@/components/Reviews';
import axios from 'axios';
import { API } from '@/config';
import { getPrefetched, getResolvedData } from '@/utils/dataPrefetcher';
import SEO from '@/components/SEO';
import { PageBreadcrumbs } from '@/components/StructuredData';
import RelatedPages from '@/components/RelatedPages';

const texts = {
  title: { sv: 'Elevrecensioner', en: 'Student Reviews', ar: 'تقييمات الطلاب', tr: 'Öğrenci Yorumları', fa: 'نظرات دانش‌آموزان', so: 'Dib-u-eegisyada Ardayda', pl: 'Recenzje uczniów', es: 'Reseñas de estudiantes', de: 'Schülerbewertungen' },
  subtitle: { sv: 'Läs verkliga elevupplevelser', en: 'Read real student experiences', ar: 'اقرأ تجارب طلابنا الحقيقية', tr: 'Gerçek öğrenci deneyimlerini okuyun', fa: 'تجربیات واقعی دانش‌آموزان را بخوانید', so: 'Akhri waayo-aragnimada dhabta ah ee ardayda', pl: 'Przeczytaj prawdziwe doświadczenia uczniów', es: 'Lee experiencias reales de estudiantes', de: 'Lies echte Schülererfahrungen' },
  reviews: { sv: 'recensioner', en: 'reviews', ar: 'تقييم', tr: 'yorum', fa: 'نظر', so: 'dib-u-eegis', pl: 'recenzji', es: 'reseñas', de: 'Bewertungen' },
  noReviews: { sv: 'Inga recensioner än', en: 'No reviews yet', ar: 'لا توجد تقييمات بعد', tr: 'Henüz yorum yok', fa: 'هنوز نظری ثبت نشده', so: 'Wali ma jiraan dib-u-eegis', pl: 'Brak recenzji', es: 'Aún no hay reseñas', de: 'Noch keine Bewertungen' },
  beFirst: { sv: 'Bli den första att skriva en recension!', en: 'Be the first to write a review!', ar: 'كن أول من يكتب تقييم!', tr: 'İlk yorumu siz yazın!', fa: 'اولین نفری باشید که نظر می‌نویسد!', so: 'Noqo kii ugu horreeyay ee qora dib-u-eegis!', pl: 'Bądź pierwszy, który napisze recenzję!', es: '¡Sé el primero en escribir una reseña!', de: 'Sei der Erste, der eine Bewertung schreibt!' },
  page: { sv: 'Sida', en: 'Page', ar: 'صفحة', tr: 'Sayfa', fa: 'صفحه', so: 'Bog', pl: 'Strona', es: 'Página', de: 'Seite' },
};

const t = (key, lang) => texts[key]?.[lang] || texts[key]?.en || key;

const ReviewsPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [reviews, setReviews] = useState(() => getResolvedData('reviews')?.reviews || []);
  const [stats, setStats] = useState(() => {
    const d = getResolvedData('reviews');
    return d ? { average_rating: d.average_rating, total_reviews: d.total_reviews } : { average_rating: 0, total_reviews: 0 };
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(() => getResolvedData('reviews')?.pages || 1);
  const [loading, setLoading] = useState(() => !getResolvedData('reviews'));

  const fetchReviews = async (p = 1) => {
    try {
      if (p === 1) {
        const prefetched = await getPrefetched('reviews');
        if (prefetched) {
          setReviews(prefetched.reviews || []);
          setStats({ average_rating: prefetched.average_rating, total_reviews: prefetched.total_reviews });
          setTotalPages(prefetched.pages || 1);
          setLoading(false);
          return;
        }
      }
      setLoading(true);
      const res = await axios.get(`${API}/reviews/public?limit=12&page=${p}`);
      setReviews(res.data.reviews || []);
      setStats({ average_rating: res.data.average_rating, total_reviews: res.data.total_reviews });
      setTotalPages(res.data.pages || 1);
    } catch { /* ignore */ } 
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReviews(page); }, [page]);

  const ratingBreakdown = [5, 4, 3, 2, 1].map(r => ({
    stars: r,
    count: reviews.filter(rv => rv.rating === r).length
  }));

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950" data-testid="reviews-page">
      <SEO page="reviews" />
      <PageBreadcrumbs page="reviews" language={i18n.language} />
      <Navbar />

      <main className="flex-grow pt-24 pb-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-slate-100 mb-3" data-testid="reviews-page-title">
              {t('title', lang)}
            </h1>
            <p className="text-base text-slate-500 dark:text-slate-400 mb-4">{t('subtitle', lang)}</p>
            
            {stats.total_reviews > 0 && (
              <div className="inline-flex items-center gap-3 bg-white dark:bg-slate-800 rounded-full px-6 py-3 shadow-sm border border-slate-100 dark:border-slate-700">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} className={`w-5 h-5 ${s <= Math.round(stats.average_rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-200 dark:text-slate-600'}`} />
                  ))}
                </div>
                <span className="text-xl font-bold text-slate-900 dark:text-slate-100">{stats.average_rating}</span>
                <span className="text-slate-400 dark:text-slate-500">|</span>
                <span className="text-slate-500 dark:text-slate-400 text-sm">{stats.total_reviews} {t('reviews', lang)}</span>
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Reviews List */}
            <div className="lg:col-span-2 space-y-4">
              {loading && reviews.length === 0 ? (
                null
              ) : reviews.length === 0 ? (
                <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700">
                  <Star className="w-12 h-12 text-slate-200 dark:text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500 dark:text-slate-400 font-medium">{t('noReviews', lang)}</p>
                  <p className="text-slate-400 dark:text-slate-500 text-sm">{t('beFirst', lang)}</p>
                </div>
              ) : (
                <>
                  {reviews.map(review => (
                    <div key={review.id} className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700 shadow-sm" data-testid="review-card">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center font-bold text-emerald-700 dark:text-emerald-400">
                            {review.user_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900 dark:text-slate-100">{review.user_name}</p>
                            <p className="text-xs text-slate-400 dark:text-slate-500">
                              {review.created_at ? new Date(review.created_at).toLocaleDateString(lang === 'ar' ? 'ar-SA' : lang === 'sv' ? 'sv-SE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200 dark:text-slate-600'}`} />
                          ))}
                        </div>
                      </div>
                      <p className="text-slate-600 dark:text-slate-300 leading-relaxed">{typeof review.comment === 'object' ? (review.comment[lang] || review.comment['en'] || review.comment['sv'] || Object.values(review.comment)[0]) : review.comment}</p>
                      <OwnerReply reply={review.admin_reply} lang={lang} />
                    </div>
                  ))}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-3 pt-4">
                      <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-slate-500 dark:text-slate-400">{t('page', lang)} {page} / {totalPages}</span>
                      <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Sidebar: Write Review */}
            <div className="lg:col-span-1">
              <div className="sticky top-28">
                <ReviewForm onSubmitted={() => fetchReviews(page)} />
              </div>
            </div>
          </div>
        </div>
      </main>

      <div className="max-w-6xl mx-auto px-4 sm:px-6"><RelatedPages currentPage="reviews" /></div>
      <Footer />
    </div>
  );
};

export default ReviewsPage;
